import { SelectionModel } from '@angular/cdk/collections'
import { HttpErrorResponse } from '@angular/common/http'
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatSort, Sort, SortDirection } from '@angular/material/sort'
import { MatRow, MatTable } from '@angular/material/table'
import { get as _get, isEqual, sortBy } from 'lodash'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  ReplaySubject,
  scan,
  share,
  Subject,
  withLatestFrom,
} from 'rxjs'

import { AdvancedTableOptions } from './advanced-table.component'
import { AdvancedTableApiService } from './advanced-table-api-service'
import { AdvancedTableDataSource } from './advanced-table-datasource'
import { CellTemplateDirective } from './cell-template.directive'
import { ColumnDef } from './column-definition'

interface AdvancedTableRxPageEvent extends PageEvent {
  startAt?: string
  total?: boolean
}

interface Sort_ {
  sortBy: string
  sortDir: SortDirection
}

function normalizeSort(params: Sort): Sort_ {
  return {
    sortBy: params.active,
    sortDir: params.direction,
  }
}

@Component({
  selector: 'mmx-advanced-table-rx',
  templateUrl: './advanced-table-rx.component.html',
  styleUrls: ['./advanced-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AdvancedTableRxComponent<RowModel>
implements AfterContentInit, AfterViewInit, OnInit, OnDestroy
{
  public static defaultOptions: AdvancedTableOptions = {
    sortable: true,
    paging: true,
    autoTriggerFirstPage: true,
    search: true,
    searchDebounce: 300,
    defaultPageSize: 15,
    filtering: false,
    selection: false,
    loadingText: 'common.please-wait',
    emptyDataText: 'common.no-data',
    rowClickable: null,
    hiddenColumns: [],
    styles: {
      stickyHeader: false,
      denseDisplay: false,
      selectedRowClass: 'ngx-mat-selected',
      tableHeaderClass: 'ngx-thead',
      tableRowHeight: '1rem',
    },
  }

  @Input() selection = new SelectionModel(true, [])
  @Input() matSortActive: string
  @Input() columns: ColumnDef<RowModel>[] = []
  @Input() displayedColumns: string[]
  @Input() dataSource: AdvancedTableDataSource<RowModel, AdvancedTableApiService<RowModel>>

  public pageChange = new Subject<AdvancedTableRxPageEvent>()
  private pageChange$ = this.pageChange.pipe(share())
  public sortChange = new Subject<Sort>()
  private sortChange$ = this.sortChange.pipe(
    map(normalizeSort),
    distinctUntilChanged(isEqual),
  )
  public busted = new Subject<boolean>()
  private resetPagination = new Subject<void>()
  private destroyed = new Subject<void>()
  private collectionErrors = new ReplaySubject<HttpErrorResponse>(1)
  private collectionSuccesses = new ReplaySubject<boolean>(1)
  private updateErrors = new Subject<HttpErrorResponse>()
  private emptyCollections = new Subject<boolean>()
  private errorStatuse$ = merge(this.collectionErrors, this.updateErrors).pipe(
    map((error) => error.statusText),
  )
  // pageSize after most recent succesful collection request
  private lastPageSize$ = this.collectionSuccesses.pipe(
    withLatestFrom(this.pageChange$, (_, pageChange) => pageChange.pageSize),
  )
  private errorsToPageChange$ = this.collectionErrors.pipe(
    withLatestFrom(this.pageChange$, (_, pageChangeEvt) => pageChangeEvt),
  )
  private errorsToLastPageSize$ = this.collectionErrors.pipe(
    withLatestFrom(this.lastPageSize$, (_, lastPageSize) => lastPageSize),
    filter(Boolean),
  )
  private emptyCollection$ = this.emptyCollections.pipe(filter(Boolean))

  @Output()
    pageUpdates = merge(this.pageChange$, this.sortChange$).pipe(
      scan((memo, obj) => ({ ...memo, ...obj }), {}),
      debounceTime(0),
    )

  // expose lodash `get()` in template
  public _get = _get

  /**
   * @description the table options as defined in NgxMatTableOptions
   * see  NgxMatTableOptionsDefaults for default values
   */
  @Input()
  set options(v: Partial<AdvancedTableOptions>) {
    this._options = v
      ? {
        ...AdvancedTableRxComponent.defaultOptions,
        ...v,
      }
      : AdvancedTableRxComponent.defaultOptions
    this.showFilters = Boolean(this._options.filtering)
  }

  get options(): Partial<AdvancedTableOptions> {
    return this._options || AdvancedTableRxComponent.defaultOptions
  }

  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort
  @ViewChild(MatTable, { static: true }) table: MatTable<RowModel>
  @ViewChildren(MatRow) tableRows: QueryList<MatRow>

  showFilters = true
  templates: Partial<CellTemplateDirective>[]
  paginationParams: Map<number, Record<string, any>> = new Map()

  // last total reported by the server for the current filter set; once known,
  // onPageChange() sends total=false to skip re-running PagingHelper's count() scan
  private knownTotal: number | undefined

  readonly selectionColumnName = 'selection'

  @ContentChildren(CellTemplateDirective)
  private _templates: QueryList<CellTemplateDirective>

  private _options: AdvancedTableOptions

  constructor(private _snackBar: MatSnackBar) {}

  ngAfterContentInit() {
    this.templates = this._templates.toArray().map((tmp) => ({
      template: tmp.template,
      name: tmp.name,
    }))
    this.buildColumns()
  }

  ngOnInit() {
    this.dataSource.collectionError$.subscribe(this.collectionErrors)
    this.dataSource.updateError$.subscribe(this.updateErrors)
    this.dataSource.collectionSuccess$.subscribe(this.collectionSuccesses)
    this.dataSource.empty$.subscribe(this.emptyCollections)
    this.dataSource.paginatonParams$.subscribe((params) => this.updatePaginationParams(this.paginator.pageIndex, params))
    this.dataSource.collectionTotal$.subscribe((total) => { this.knownTotal = total })
    // show snackbar message for errors
    this.errorStatuse$.subscribe(this.snackMessage.bind(this))
    // reset pageIndex to most recent after an error
    this.errorsToPageChange$.subscribe(
      (pageChangeEvent) =>
        (this.paginator.pageIndex = pageChangeEvent.previousPageIndex || 0),
    )
    // reset pageSize to most recent after an error
    this.errorsToLastPageSize$.subscribe(
      (pageSize) => (this.paginator.pageSize = pageSize),
    )
    // remove busted state when pagination reset
    this.resetPagination.pipe(map(() => false)).subscribe(this.busted)
    /* conditions to reset pagination to initial state:
    1 sort parameters change
    2 the resetPagination subject emits
    3 the response has 0 length, eg. after a filter with no matches
    */
    merge(
      this.sortChange$,
      this.resetPagination,
      // @TODO: this is not working, it just triggers an infinite loop
      // this.emptyCollection$,
    ).subscribe(() => {
      // on fresh sort/filter/search, we don't know the total for the new filter set, so clear it      
      this.knownTotal = undefined
      this.initialPaginationState(0)
    })
  }

  ngAfterViewInit() {
    this.initialPaginationState()
    this.initialSortState()
  }

  notInHidden(item: ColumnDef<RowModel>) {
    return !this.options.hiddenColumns.includes(item.key)
  }

  templateForColumn(
    column: ColumnDef<RowModel>,
  ): Partial<CellTemplateDirective> | undefined {
    return this.templates.find((template) => template.name === column.key)
  }

  rowClicked(row: RowModel, event: MouseEvent) {
    if (this.options.selection) {
      this.selection.toggle(row)
    } else if (this.options.rowClickable != null) {
      this.options.rowClickable(row, event)
    }
  }

  private buildColumns() {
    if (!this.columns) {
      return
    }
    this.sortColumns()
    this.displayedColumns = this.columns
      .filter(
        (col) => col.visible && !this.options.hiddenColumns.includes(col.key),
      )
      .map((col) => col.key)

    if (
      this.options.selection &&
      !this.displayedColumns.includes(this.selectionColumnName)
    ) {
      this.displayedColumns = [
        this.selectionColumnName,
        ...this.displayedColumns,
      ]
    }
  }

  private sortColumns() {
    this.columns = sortBy(this.columns, ['order'])
  }

  reloadPagination() {
    this.resetPagination.next()
  }

  onPageChange(event: PageEvent) {
    const paginationParams = this.paginationParams.get(event.previousPageIndex)

    this.pageChange.next({
      ...event,
      startAt: event.pageSize === paginationParams.pageSize ? paginationParams.startAt : undefined,
      // skip the server's count() scan once we already know the total for this filter set
      total: this.knownTotal == null,
    })

    this.updatePaginationParams(event.pageIndex, {
      pageSize: event.pageSize,
    })
  }

  private initialPaginationState(initialPage?: number) {
    const { pageSize, length } = this.paginator
    const pageIndex = initialPage ?? this.paginator.pageIndex
    //Added startAt to the pageChange event so that the stale startAt issue on sort/filter/search gets globally fixed. 
    // The startAt is now derived from the paginationParams map which is updated on every pageChange event.
    const startAt = pageIndex === 0 ? undefined : this.paginationParams.get(pageIndex)?.startAt
    this.paginator.pageIndex = pageIndex
    this.updatePaginationParams(pageIndex, {
      pageSize,
    })
    // explicit true (not omitted) so a stale total=false from a prior page navigation
    // can't survive in the pageUpdates scan accumulator, same class of bug as startAt above
    this.pageChange.next({ pageIndex, pageSize, length, startAt, total: true })
  }

  private initialSortState() {
    const { active, start } = this.sort
    this.sortChange.next({ active, direction: start })
  }

  private snackMessage(message: string): void {
    this._snackBar.open(message, 'OK', {
      duration: 3000,
      verticalPosition: 'top',
    })
  }

  private updatePaginationParams(pageIndex: number, params: Record<string, any>) {
    this.paginationParams.set(pageIndex, {
      ...(this.paginationParams.get(pageIndex) || {}),
      ...params,
    })
  }

  ngOnDestroy() {
    this.destroyed.next()
  }
}
